/**
 * Feature flag definitions.
 *
 * Extends the backend FeatureFlagService defaults with shared definitions
 * used by the frontend. When the API is unreachable, these defaults are
 * used as a fallback so the app still functions.
 */
export interface FeatureFlags {
  [key: string]: boolean;
}

export const FEATURE_FLAGS = {
  AUDIT_TRAIL: { key: 'audit-trail', default: true, description: 'Audit trail for all domain events' },
  MFA_STEP_UP: { key: 'mfa-step-up', default: true, description: 'MFA step-up for sensitive operations' },
  ONBOARDING: { key: 'onboarding', default: true, description: 'Tenant onboarding flow' },
  IMPORT: { key: 'import', default: true, description: 'Data import with preview and line-level errors' },
  NOTIFICATIONS: { key: 'notifications', default: true, description: 'In-app and email notifications' },
  WEBHOOKS: { key: 'webhooks', default: true, description: 'Outbound webhook delivery' },
  DOCUMENT_OCR: { key: 'document-ocr', default: false, description: 'OCR on uploaded documents' },
  BI_REPORTS: { key: 'bi-reports', default: false, description: 'BI and analytics reports' },
  OFFLINE_POS: { key: 'offline-pos', default: true, description: 'Offline PDV mode with sync' },
  LOT_SERIAL: { key: 'lot-serial', default: false, description: 'Lot and serial number tracking' },
  CONTRACT_RECURRENCE: { key: 'contract-recurrence', default: false, description: 'Recurring contracts' },
  BUDGET_CONTROL: { key: 'budget-control', default: false, description: 'Budget control per account' },
  MANUFACTURING: { key: 'manufacturing', default: false, description: 'Manufacturing / production orders' },
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

/** Array form of FEATURE_FLAGS for iteration (e.g. debug panels). */
export const FEATURE_FLAGS_LIST = Object.values(FEATURE_FLAGS);

export function getDefaultFlags(): Record<string, boolean> {
  const result: Record<string, boolean> = {};
  for (const [, value] of Object.entries(FEATURE_FLAGS)) {
    result[value.key] = value.default;
  }
  return result;
}
